import { codeUnitString } from "./code-unit-string.js";
import { AUDIT_CANONICAL_ID_PATTERN, AUDIT_ENC_FIELD_MAX_BYTES, auditCanonicalIdSchema, isAuditCanonicalId } from "./audit-envelope.js";
import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/audit-archive-layout.ts
/**
* The audit archive's object layout, seal chain and parquet schema v1 (feature
* `audit-sink`, Stage A; `00-index.md` 1.3, rulings R-007, R-008, R-013, AD-14).
*
* Layout, under `gs://dp-<xr>-audit-events/` (or the MinIO bucket locally):
*
*   org=<orgId>/date=<YYYY-MM-DD>/<sha256>.parquet          one segment, named by its bytes
*   org=<orgId>/date=<YYYY-MM-DD>/_seal/<seqLast>-<sha256>.json   one chain entry per seal, append-only
*   org=<orgId>/date=<YYYY-MM-DD>/_manifest.json             once, at day close
*   _schema/v1.json                                          once, the parquet schema, revision 1
*   _schema/v1.r<revision>.json                              once per later revision of the schema
*
* Every object is written with a create-only precondition by an identity that can
* create and nothing else. The day is the UTC date of `recorded_at`, the
* JetStream message timestamp, which is stable across redelivery (R-008).
*
* The chain. Each seal record carries `entryIndex` (0-based and contiguous within
* the org-day), `prevDigest` (the previous record's `digest` in the same org-day,
* or the previous day's `dayDigest`, or `null`) and `digest`, the SHA-256 of its
* own RFC 8785 canonical JSON with `digest` removed. Stream sequences are bounds,
* strictly increasing across entries and never contiguous: `ZION_AUDIT` is one
* stream with one global sequence shared by every org, so an org's chain skips
* the sequences other orgs used. {@link auditChainCanonicalJson} is the canonical
* form; the Rust archiver mirrors it and the fixture pins vectors so both sides
* agree byte for byte.
*
* The archiver re-derives chain state from the bucket (R-017): on an org-day's
* first message it lists `_seal/` under that day, whose zero-padded sequence
* prefix makes lexical order chain order, and reads the tail record.
*
* The root barrel re-exports this module, and browser bundles import that barrel
* through `@zion/sdk-react`, so this module imports no Node builtin and references no
* Node global (#3073). The SHA-256 comes from `@noble/hashes`, which is synchronous and
* runs in both Node and the browser. `barrel-browser-safe.test.ts` holds both rules for
* the whole barrel.
*/
/** The parquet schema version this layout writes. Evolution is additive only. */
const AUDIT_ARCHIVE_SCHEMA_VERSION = 1;
/**
* The revision of parquet schema v1 this layout writes: the count of additive changes inside
* version 1. Revision 1 is the 22-column table of feature audit-sink. Revision 2 adds columns 23 to
* 26, the verdict columns of 2026-10-02-directory-asks-platform (I12). Revision 3 adds column 27,
* `related_object_id`, and its bloom filter (2026-10-06-audit-second-id-filter, D-3). A new
* column appends and moves the revision by one. {@link AUDIT_ARCHIVE_SCHEMA_VERSION} and the day
* manifest's `schemaVersion` stay 1. The declaration stays on one line in this exact form: the
* archiver's `envelope_contract.rs` reads the line as text and compares it with its own
* `PARQUET_SCHEMA_REVISION`.
*/
const AUDIT_PARQUET_SCHEMA_REVISION = 3;
/**
* The object key that carries revision `revision` of parquet schema v1.
*
* - `1`: `_schema/v1.json`, the key feature audit-sink wrote, so a bucket from before revision 2
*   keeps the object it holds and that object stays true for the 22 columns it lists.
* - An integer of 2 or more: `_schema/v1.r<revision>.json`, for example `_schema/v1.r2.json`.
* - Any other number (0, a negative, a fraction, `NaN`): throws an `Error` that names the value.
*
* Every revision is its own create-only object, so a revision bump writes a new object and never
* needs an overwrite, which the archive's create-only identity cannot do. A reader takes the key of
* the revision it needs. The Rust archiver's `schema_object_key` in `zion-archive-core` answers the
* same keys. Pure.
*/
function auditSchemaObjectKey(revision) {
	if (!Number.isInteger(revision) || revision < 1) throw new Error(`auditSchemaObjectKey: revision is not a positive integer: ${String(revision)}`);
	return revision === 1 ? "_schema/v1.json" : `_schema/v1.r${revision}.json`;
}
/** The object that carries the parquet schema this layout writes: `_schema/v1.r3.json`. */
const AUDIT_SCHEMA_OBJECT_KEY = auditSchemaObjectKey(3);
/** The org grammar is the one id grammar (R-038); this alias keeps the key builders readable. */
const ORG_ID_RE = AUDIT_CANONICAL_ID_PATTERN;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/**
* `true` for a `YYYY-MM-DD` string naming a day the calendar has and a Postgres `date` column
* holds (R-074, R-077). `2026-02-31` and `2026-00-00` match `DATE_RE` and reach the column as a
* 500; `0000-01-01` round-trips through `Date` and the column refuses it too (`22008`), so the
* floor is `AUDIT_MIN_CALENDAR_DAY`. core-api's `isColumnCalendarDay` is the same rule.
*/
const AUDIT_MIN_CALENDAR_DAY = "0001-01-01";
const isCalendarDay = (d) => {
	if (!DATE_RE.test(d) || d < "0001-01-01") return false;
	const ms = Date.parse(`${d}T00:00:00Z`);
	return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === d;
};
const SHA256_HEX_RE = /^[0-9a-f]{64}$/;
const DIGEST_RE = /^sha256:[0-9a-f]{64}$/;
/** The largest stream sequence a record may carry: JetStream's `u64` bounded to what JSON keeps exact. */
const AUDIT_MAX_STREAM_SEQ = Number.MAX_SAFE_INTEGER;
function assertOrgId(orgId, fn) {
	if (!isAuditCanonicalId(orgId)) throw new Error(`${fn}: orgId is not a canonical UUID: ${JSON.stringify(orgId)}`);
}
function assertDate(date, fn) {
	if (!DATE_RE.test(date)) throw new Error(`${fn}: date is not YYYY-MM-DD: ${JSON.stringify(date)}`);
}
function assertSha256(hex, fn) {
	if (!SHA256_HEX_RE.test(hex)) throw new Error(`${fn}: sha256 is not 64 lowercase hex chars`);
}
/** `org=<orgId>/`. Every per-tenant operation (export, IAM, retention) is a prefix operation on this. */
function auditOrgPrefix(orgId) {
	assertOrgId(orgId, "auditOrgPrefix");
	return `org=${orgId}/`;
}
/** `org=<orgId>/date=<YYYY-MM-DD>/`. The day is the UTC date of `recorded_at`. */
function auditDayPrefix(orgId, date) {
	assertDate(date, "auditDayPrefix");
	return `${auditOrgPrefix(orgId)}date=${date}/`;
}
/** The segment key. The name is the SHA-256 of the file's bytes, so identical bytes land on one name. */
function auditSegmentKey(orgId, date, sha256Hex) {
	assertSha256(sha256Hex, "auditSegmentKey");
	return `${auditDayPrefix(orgId, date)}${sha256Hex}.parquet`;
}
/**
* The seal record key: `_seal/<seqLast as 20 zero-padded digits>-<sha256>.json`.
* Zero-padding keeps a lexical listing in sequence order. `seqLast` is the
* segment's last stream sequence, or the quarantined message's sequence, and is
* bounded by {@link AUDIT_MAX_STREAM_SEQ} like every record field; the 20-digit
* pad is sized for a u64 so the key grammar survives a later widening.
* `sha256Hex` is the segment's `sha256` for a segment record and the 64 hex
* chars of the record's own `digest` for a quarantine record (R-025).
*/
function auditSealKey(orgId, date, seqLast, sha256Hex) {
	if (typeof seqLast === "number" && !Number.isSafeInteger(seqLast)) throw new Error("auditSealKey: seqLast is not an integer");
	const seq = typeof seqLast === "bigint" ? seqLast : BigInt(seqLast);
	if (seq < 0n || seq > BigInt(AUDIT_MAX_STREAM_SEQ)) throw new Error("auditSealKey: seqLast outside 0..AUDIT_MAX_STREAM_SEQ");
	assertSha256(sha256Hex, "auditSealKey");
	return `${auditDayPrefix(orgId, date)}_seal/${seq.toString().padStart(20, "0")}-${sha256Hex}.json`;
}
/** The day manifest key, written once when the day closes. */
function auditManifestKey(orgId, date) {
	return `${auditDayPrefix(orgId, date)}_manifest.json`;
}
/**
* Why a segment sealed: the age tick, the per-buffer byte cap, the row cap, the UTC
* day boundary, or the global open-buffer cap sealing the largest buffer (R-043).
*/
const AUDIT_SEAL_REASONS = [
	"age",
	"bytes",
	"rows",
	"day",
	"overflow"
];
/** A segment record names at most this many publisher key ids, each at most this long (R-034). */
const AUDIT_PUBLISHER_KIDS_MAX = 64;
const AUDIT_PUBLISHER_KID_MAX_CHARS = 256;
const digestField = z.string().regex(DIGEST_RE, "digest: sha256:<64 hex>");
const streamSeq = z.number().int().min(0).max(AUDIT_MAX_STREAM_SEQ);
/**
* The key of one `actionCounts` entry on a segment record: `<action>|<source>`, where the
* source is the row's `source` column (`platform` or `app`). The archiver writes the map at
* seal time from the rows in memory, so a reader answers `total` and `filteredOut` for a
* closed org-day from the seal records alone (audit-reader-cutover I-6). Older records carry
* no map, and a reader falls back to scanning their segments.
*/
const AUDIT_ACTION_COUNT_KEY_RE = /^[^|\s]{1,256}\|(platform|app)$/;
const AUDIT_ACTION_COUNT_SOURCES = ["platform", "app"];
/**
* The most `actionCounts` keys one seal record may carry (R-066). The archiver's
* `ACTION_COUNTS_MAX_KEYS` pins the same figure and stops counting at it. What the figure
* prices: a key is at most 73 characters (the envelope's 64-character `action`, `|`, `platform`),
* so one record's map is under 100 KB. A day manifest inlines every record: 288 at the 300-second
* seal age, plus one per 256 MiB of buffered bytes past that rate (3,219 a day at 10 MB/s for one
* org, about 320 MB of manifest at the cap). The reader bounds its closed-day cache by bytes, so
* the manifest's size reaches no reader budget; records out of the manifest are the stage-2
* layout item (R-046, R-073).
*/
const AUDIT_ACTION_COUNTS_MAX_KEYS = 1024;
/** The `actionCounts` key for one `(action, source)` pair. Throws on a key the regex refuses. */
function auditActionCountKey(action, source) {
	const key = `${action}|${source}`;
	if (!AUDIT_ACTION_COUNT_KEY_RE.test(key)) throw new Error(`auditActionCountKey: ${JSON.stringify(key)} is not <action>|<source>`);
	return key;
}
const actionCounts = z.record(z.string().regex(AUDIT_ACTION_COUNT_KEY_RE, "actionCounts key: <action>|<source>"), z.number().int().min(1)).refine((m) => Object.keys(m).length <= AUDIT_ACTION_COUNTS_MAX_KEYS, { message: `actionCounts: at most ${AUDIT_ACTION_COUNTS_MAX_KEYS} keys` });
const sealRecordCommon = {
	v: z.literal(1),
	org: z.string().regex(ORG_ID_RE),
	date: z.string().regex(DATE_RE),
	/** 0-based position in the org-day's chain; contiguous, so a removed entry leaves a hole (R-019). */
	entryIndex: z.number().int().min(0).max(AUDIT_MAX_STREAM_SEQ),
	prevDigest: digestField.nullable(),
	digest: digestField
};
/**
* A segment entry: one parquet file, its sequence range, both clocks' bounds,
* the reason it sealed, and the publisher key ids seen in it. `seqFirst <= seqLast`.
*/
const auditSegmentSealRecordV1Schema = z.object({
	...sealRecordCommon,
	kind: z.literal("segment"),
	seqFirst: streamSeq,
	seqLast: streamSeq,
	key: z.string().min(1),
	sha256: z.string().regex(SHA256_HEX_RE),
	rows: z.number().int().min(1),
	bytes: z.number().int().min(1),
	minRecordedAt: z.string().datetime(),
	maxRecordedAt: z.string().datetime(),
	minOccurredAt: z.string().datetime().optional(),
	maxOccurredAt: z.string().datetime().optional(),
	sealReason: z.enum(AUDIT_SEAL_REASONS),
	publisherKids: z.array(codeUnitString().min(1).max(256)).max(64),
	sealedAt: z.string().datetime(),
	/** Rows per `<action>|<source>` in this segment; absent on records sealed before the reader cutover. */
	actionCounts: actionCounts.optional()
}).strict().refine((r) => r.seqFirst <= r.seqLast, {
	message: "seal record: seqFirst must not exceed seqLast",
	path: ["seqLast"]
});
/**
* A quarantine entry: one message the archiver refused (schema, org mismatch, or
* the fifth delivery), the DLQ subject it went to, and why. `org` may be
* `unknown` when the subject's org token was not a UUID.
*/
const auditQuarantineSealRecordV1Schema = z.object({
	...sealRecordCommon,
	org: z.union([z.string().regex(ORG_ID_RE), z.literal("unknown")]),
	kind: z.literal("quarantine"),
	streamSeq,
	reason: codeUnitString().min(1).max(512),
	dlqSubject: z.string().min(1),
	at: z.string().datetime()
}).strict();
/**
* The advisory record the archiver publishes to `zion.dlq.zion.audit.<subjectOrg>` for a
* refused message (R-031). It carries no byte of the refused message: the digest and
* the length identify it, the reason says why, and `eventId` is present when the
* payload parsed far enough to carry one. The quarantine seal record in the bucket is
* the durable record; this copy is what a `DLQ_READ_PERM` holder sees.
*/
const AUDIT_QUARANTINE_DLQ_KIND = "audit-quarantine";
const auditQuarantineDlqRecordV1Schema = z.object({
	v: z.literal(1),
	kind: z.literal(AUDIT_QUARANTINE_DLQ_KIND),
	streamSeq,
	subject: codeUnitString().min(1).max(256),
	subjectOrg: z.union([auditCanonicalIdSchema, z.literal("unknown")]),
	reason: codeUnitString().min(1).max(512),
	sha256: z.string().regex(SHA256_HEX_RE),
	byteLength: z.number().int().min(0),
	eventId: auditCanonicalIdSchema.optional()
}).strict();
/** A chain entry is a segment record or a quarantine record. */
const auditSealRecordV1Schema = z.union([auditSegmentSealRecordV1Schema, auditQuarantineSealRecordV1Schema]);
/**
* The day manifest: every chain entry of the org-day in order, the last entry's
* digest as `dayDigest`, the previous day's as `prevDayDigest`, and the publisher
* JWKs the day's `publisherKids` resolve to (empty until Stage D records them).
*/
const auditDayManifestV1Schema = z.object({
	v: z.literal(1),
	org: z.string().regex(ORG_ID_RE),
	date: z.string().regex(DATE_RE),
	schemaVersion: z.literal(1),
	entries: z.array(auditSealRecordV1Schema),
	dayDigest: digestField.nullable(),
	prevDayDigest: digestField.nullable(),
	closedAt: z.string().datetime(),
	publisherJwks: z.array(z.unknown())
}).strict();
/**
* RFC 8785 (JCS) canonical JSON for the value shapes a chain record uses:
* objects with keys sorted by UTF-16 code units, arrays in order, strings as
* `JSON.stringify` renders them, integers as digits, booleans and `null`.
* Non-integer numbers throw: a chain record carries none, and refusing keeps the
* Rust mirror trivially equal. `undefined` object values are omitted, as
* `JSON.stringify` omits them.
*/
function auditChainCanonicalJson(value) {
	if (value === null) return "null";
	switch (typeof value) {
		case "string": return JSON.stringify(value);
		case "boolean": return value ? "true" : "false";
		case "bigint":
			if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < -BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("auditChainCanonicalJson: only safe integers are canonical");
			return value.toString();
		case "number":
			if (!Number.isSafeInteger(value)) throw new Error("auditChainCanonicalJson: only safe integers are canonical");
			return String(value);
		case "object": {
			if (Array.isArray(value)) return `[${value.map((v) => auditChainCanonicalJson(v)).join(",")}]`;
			const obj = value;
			return `{${Object.keys(obj).filter((k) => obj[k] !== void 0).sort().map((k) => `${JSON.stringify(k)}:${auditChainCanonicalJson(obj[k])}`).join(",")}}`;
		}
		default: throw new Error(`auditChainCanonicalJson: unsupported value type ${typeof value}`);
	}
}
/**
* `sha256:<hex>` over the canonical JSON of `record` with its `digest` field
* removed. Verifying a chain entry recomputes this and compares; the next entry's
* `prevDigest` must equal it.
*/
function auditChainDigest(record) {
	const { digest: _omitted, ...rest } = record;
	return `sha256:${bytesToHex(sha256(utf8ToBytes(auditChainCanonicalJson(rest))))}`;
}
/**
* Verifies a chain in append order. For every entry: when `scope` is given, `org` and
* `date` equal it; `entryIndex` is the previous entry's plus one (the first equals
* `expectedFirstIndex`); `prevDigest` equals the previous entry's `digest` (the
* first equals `expectedPrevDigest`); and `digest` recomputes. A manifest check
* passes the manifest's own `org` and `date` as the scope. Returns the first
* problem, or `null` when the chain holds. An empty chain holds.
*
* Append order is `entryIndex` order, and the `_seal/` listing is lexical on `seqLast`,
* so a caller that read the bucket sorts with `auditChainInAppendOrder` first. The
* stream sequences of consecutive entries carry no rule here (audit-reader-cutover
* I-5, #3349): after a restart the archiver seals a redelivered lower range after a
* higher one, and a span it could not read back is sealed again, so spans overlap.
* Completeness is a question about the set, answered by `auditChainSpans`; a reader
* dedupes rows by `stream_seq`.
*/
function auditVerifyChain(entries, expectedPrevDigest, expectedFirstIndex = 0, scope) {
	let prev = expectedPrevDigest;
	let nextIndex = expectedFirstIndex;
	for (let i = 0; i < entries.length; i += 1) {
		const e = entries[i];
		if (scope !== void 0 && (e.org !== scope.org || e.date !== scope.date)) return {
			index: i,
			problem: `entry belongs to ${e.org}/${e.date}, scope is ${scope.org}/${scope.date}`
		};
		if (e.entryIndex !== nextIndex) return {
			index: i,
			problem: `entryIndex ${e.entryIndex} expected ${nextIndex}`
		};
		if (e.prevDigest !== prev) return {
			index: i,
			problem: `prevDigest ${String(e.prevDigest)} does not match ${String(prev)}`
		};
		const recomputed = auditChainDigest(e);
		if (recomputed !== e.digest) return {
			index: i,
			problem: `digest ${e.digest} recomputes to ${recomputed}`
		};
		prev = e.digest;
		nextIndex += 1;
	}
	return null;
}
/**
* The chain in append order: sorted by `entryIndex`, ties kept in the given order. The
* `_seal/` listing is lexical on the zero-padded `seqLast`, which stops being append
* order the first time a restart seals a lower range after a higher one.
*/
function auditChainInAppendOrder(entries) {
	return [...entries].sort((a, b) => a.entryIndex - b.entryIndex);
}
/**
* The union of the stream sequences an org-day's chain claims, as disjoint inclusive
* spans sorted ascending; touching and overlapping spans merge. This is the
* completeness answer for a chain whose entries may overlap: a sequence the stream
* delivered to this org and no span covers is missing from the archive. Segments
* contribute `[seqFirst, seqLast]`, quarantine records `[streamSeq, streamSeq]`.
*/
function auditChainSpans(entries) {
	const spans = entries.map((e) => e.kind === "segment" ? {
		first: e.seqFirst,
		last: e.seqLast
	} : {
		first: e.streamSeq,
		last: e.streamSeq
	}).sort((a, b) => a.first - b.first || a.last - b.last);
	const merged = [];
	for (const span of spans) {
		const prev = merged[merged.length - 1];
		if (prev !== void 0 && span.first <= prev.last + 1) {
			if (span.last > prev.last) merged[merged.length - 1] = {
				first: prev.first,
				last: span.last
			};
		} else merged.push(span);
	}
	return merged;
}
/** The page caps the admin audit route already enforces; the reader refuses past them too. */
const AUDIT_READER_MAX_PAGE_SIZE = 200;
const AUDIT_READER_MAX_PAGE = 1e5;
/** A filter list the reader receives (the view's exclusion sets) is bounded. */
const AUDIT_READER_MAX_FILTER_TERMS = 1024;
/**
* The actions of the rows that an `objectIdAliases` entry admits (2026-10-31-activity-app-filter,
* D11 as R-007 and R-008 narrow it). An entry admits a row only when two facts hold:
*
* 1. The row's `action` is one of these two platform grant verbs.
* 2. The row's `source` is `'platform'`.
*
* Any other row passes the `objectId` term on `objectId` alone. An app writes its free-text
* `resourceId` into `object_id`. So without these rules, an app row that names a grant id of another
* app passes that app's filter. The `source` test also covers an app declaration of a grant verb
* from before release-engine refused platform verbs (`isPlatformAuditVerb`).
*/
const AUDIT_READER_OBJECT_ID_ALIAS_ACTIONS = ["resource_grant.granted", "resource_grant.revoked"];
/**
* The bound on every text column the reader returns and every filter term it receives: the
* envelope's widest text cap (`objectId`, 512), so a row the archiver sealed always parses
* (R-028). The envelope bounds the other text fields tighter at seal time.
*/
const AUDIT_READER_MAX_TERM_CHARS = 512;
/**
* The longest ciphertext column the reader may return: `AUDIT_ENC_FIELD_MAX_BYTES` (66,048)
* decoded bytes as padded base64, 88,064 characters. The producer caps the decoded bytes
* (`auditEncryptedFieldsSchema`), so a longer column is tampering, and both twins refuse it
* with `archive_integrity` (R-057).
*/
const AUDIT_READER_MAX_CIPHER_CHARS = Math.ceil(AUDIT_ENC_FIELD_MAX_BYTES / 3) * 4;
/** The most data subjects one row may name: the envelope's `dataSubjects` cap (R-057). */
const AUDIT_READER_MAX_ROW_SUBJECTS = 16;
/**
* The two actor views. `appRowActor` narrows on `source` alone, as `AuditMergedListFilter` does;
* `variesActor.actor` takes the same two values.
*/
const AUDIT_READER_ROW_ACTORS = ["people", "system"];
/** The two refusals the reader answers with, and the HTTP status each carries. */
const AUDIT_READER_REFUSAL_CODES = ["window_too_wide", "archive_integrity"];
/**
* The longest `cursor` a request may carry and the longest `nextCursor` an answer may carry, in
* characters (activity-paging decision 5). The reader mints the cursor and core-api passes it
* through unread, so this cap and {@link AUDIT_READER_CURSOR_RE} are the whole of what the two
* contracts say about its bytes. The dp-admin route applies the same cap to its `cursor` key.
* The reader's request body cap (`server::MAX_REQUEST_BYTES`) prices this cap and the two
* `eventTypes` caps below, so a body at every cap reaches the parser (activity-paging R-002).
*/
const AUDIT_READER_MAX_CURSOR_CHARS = 512;
/**
* The alphabet of a cursor: base64url with no padding, `A-Z`, `a-z`, `0-9`, `-` and `_`. A cursor
* with any other character is refused at both wires before the reader reads anything (decision 5,
* defaults 1 and 3 of the activity-paging index). The reader's cursor module mints in this
* alphabet, and its own check of the decoded bytes comes after this one.
*/
const AUDIT_READER_CURSOR_RE = /^[A-Za-z0-9_-]+$/;
/**
* The most entries one `eventTypes` list may carry (decision 6). The widest curated Activity tab
* names 18 types; 64 leaves room for a tab to grow and keeps the per-day prune cheap.
*/
const AUDIT_READER_MAX_EVENT_TYPES = 64;
/**
* The longest one `eventTypes` entry may be, in UTF-16 code units (decision 6). It is the
* dp-admin route's `eventType` cap and the action bound of {@link AUDIT_ACTION_COUNT_KEY_RE}, so
* every type the route admits can match an `actionCounts` key.
*/
const AUDIT_READER_MAX_EVENT_TYPE_CHARS = 256;
const filterTerm = codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS);
/** A cursor on the wire: 1 to {@link AUDIT_READER_MAX_CURSOR_CHARS} base64url characters. */
const cursorValue = codeUnitString().min(1).max(512).regex(AUDIT_READER_CURSOR_RE, "cursor: base64url with no padding");
/** One `eventTypes` entry: 1 to {@link AUDIT_READER_MAX_EVENT_TYPE_CHARS} UTF-16 code units. */
const eventTypeTerm = codeUnitString().min(1).max(256);
const filterTerms = z.array(filterTerm).max(AUDIT_READER_MAX_FILTER_TERMS);
const isoInstant = z.string().datetime();
/** A ciphertext column as the reader returns it: the parquet bytes, base64, with the producer's version byte kept. */
const cipherColumn = codeUnitString().min(4).max(AUDIT_READER_MAX_CIPHER_CHARS).regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/, "ciphertext: base64");
/** The fraction digits the reader keeps: nanoseconds, the finest the Rust `time` crate holds. */
const INSTANT_FRACTION_DIGITS = 9;
/**
* Whether the instant `a` is after the instant `b`, for two values `isoInstant` already admits.
*
* The order is the order of the parsed instants at the reader's precision, so the two
* validators of this wire refuse the same windows (R-081, R-086). A string order is wrong for
* mixed precision: it ranks `10:00:00.5Z` before `10:00:00Z`.
* `Date.parse` keeps milliseconds only, so the whole seconds come from it and the fraction is
* compared as digits, cut to nine the way `QueryRequestV1::validate` cuts it.
*/
function auditInstantIsAfter(a, b) {
	const [aSeconds, aFraction] = instantParts(a);
	const [bSeconds, bFraction] = instantParts(b);
	return aSeconds === bSeconds ? aFraction > bFraction : aSeconds > bSeconds;
}
/** The whole-second epoch milliseconds and the fraction as nine digits, for an `isoInstant` value. */
function instantParts(value) {
	const dot = value.indexOf(".");
	const whole = dot === -1 ? value : `${value.slice(0, dot)}Z`;
	const fraction = dot === -1 ? "" : value.slice(dot + 1, -1);
	return [Date.parse(whole), fraction.slice(0, INSTANT_FRACTION_DIGITS).padEnd(INSTANT_FRACTION_DIGITS, "0")];
}
/**
* What core-api asks the reader. `org` comes from the caller's verified JWT and is
* the only tenant scope; the exclusion sets are the level and actor view compiled by
* core-api from the vocabulary and the app declarations (`viewFilter`), so the reader
* holds no vocabulary. `userId` matches `actor_id`, `object_id` or `related_object_id`
* (2026-10-06-audit-second-id-filter, D-1); a segment written before revision 3 has no
* `related_object_id` column, and the reader reads it as null there (D-10). The request shape
* did not change for that widening, so a reader and a core-api of different releases agree on
* every key. `eventType` matches `action`. Strict: an unknown key is refused before it reaches
* the reader.
*
* Two modes (activity-paging decisions 1 to 3). The keyset mode is a request that carries a
* `cursor` or `includeTotal: false`. Every other request is the old mode, today's request, and
* the reader answers it byte for byte as before: offset paging by `page` and `pageSize`, exact
* `total` and `filteredOut`, no `nextCursor`, no `searchedBackTo`.
*
* An old-mode request with `includeTotal: true` gets the same rows, `total` and `filteredOut`, plus
* `countsExact: true`. Its segment budget charges only the segments the reader reads, and a
* request without the key charges every planned segment (activity-paging decision 15, R-005). So
* the reader can serve a request with `includeTotal: true` where it refuses the same request
* without the key with `window_too_wide`.
*
* The keyset mode answers up to `pageSize` rows newest first, starting after the cursor's
* position (or at the newest row when no cursor is sent), with `nextCursor`, `searchedBackTo` and
* `countsExact: false`; `total` and `filteredOut` are then floors over the span the reader read.
* The keyset mode charges the per-query segment and byte budget on what it plans and reads
* (activity-paging R-022). When the first day the page covers passes either limit alone, the
* answer is the 422 `window_too_wide`, as in the old mode. A later day that would pass a limit
* ends the walk: the answer carries the rows it holds, `searchedBackTo` and a `nextCursor`.
*
* Refusals beside the field bounds, each an issue on the named path:
* 1. `eventType` and `eventTypes` together (path `eventTypes`).
* 2. The keyset mode with a `page` other than 1 (path `page`): a cursor names the position, so a
*    page number would be a second, conflicting one.
* 3. A `cursor` with `includeTotal: true` (path `includeTotal`): a next page owes no exact total;
*    the caller asks for the total with an old-mode request.
* 4. `objectIdAliases` without `objectId` (path `objectIdAliases`): the aliases only widen the
*    `objectId` term (2026-10-31-activity-app-filter, D4).
* The reader's `QueryRequestV1::validate` refuses the same requests with a 400 naming the field.
*
* Release order (activity-paging R-010). The reader ships on the CP plane and core-api on the DP
* plane. A reader built before the keyset mode refuses `cursor`, `eventTypes` and `includeTotal`
* as unknown keys, so the admin app that sends them ships after the reader and rolls back first.
*/
const auditReaderQueryRequestV1Schema = z.strictObject({
	v: z.literal(1),
	org: z.string().regex(ORG_ID_RE),
	startDate: isoInstant.optional(),
	endDate: isoInstant.optional(),
	userId: filterTerm.optional(),
	/**
	* Matches `object_id` exactly, byte for byte, and nothing else (2026-10-21-app-open-audit,
	* D13). It combines by AND with every other filter, `userId` included, so one request can
	* ask for one person's rows about one subject. The reader does three things with it:
	*
	* - it prunes row groups with the existing `object_id` bloom filter;
	* - it applies the same predicate in its count;
	* - it adds the term to the cursor binding only when the request sets it.
	*
	* So every cursor minted without the term stays valid. core-api sends it only when an admin read asks for `appId`, so a reader
	* built before this key refuses no other read. Absent means no subject constraint.
	*/
	objectId: filterTerm.optional(),
	/**
	* More ids that pass the `objectId` term (2026-10-31-activity-app-filter, D4). A row passes the
	* term in two cases, byte for byte:
	*
	* - its `object_id` equals `objectId`, whatever its action;
	* - its `object_id` equals one entry, its `action` is one of
	*   {@link AUDIT_READER_OBJECT_ID_ALIAS_ACTIONS} (R-007), and its `source` is `'platform'`
	*   (R-008).
	*
	* core-api fills it with the ids of the app's `auth.resource_grants` rows, revoked rows
	* included. A `resource_grant.granted` or `resource_grant.revoked` row carries the grant row id
	* as its `object_id`, and only its sealed `details` hold the app id. So the list lets an `appId`
	* read return the grant rows of the app.
	*
	* Rules:
	* 1. 1 to {@link AUDIT_READER_MAX_FILTER_TERMS} entries, each a lower-case canonical id.
	* 2. Duplicate entries are one entry.
	* 3. The key must come with `objectId`. The schema refuses it alone on this path: alone, it
	*    would bind the cursor of the unfiltered request and return other rows.
	* 4. The reader prunes with the `object_id` bloom filter and tests `objectId` and every entry
	*    on one fetch of it. Its count applies the same predicate.
	* 5. The reader leaves the key out of the cursor binding (D5). core-api derives the list from
	*    `objectId`, so a grant made between two pages keeps the cursor valid.
	* 6. core-api sends the key only when the list is not empty (D7). A reader built before this
	*    key then refuses no read of an app with no grant.
	*
	* Absent means no alias.
	*/
	objectIdAliases: z.array(auditCanonicalIdSchema).min(1).max(AUDIT_READER_MAX_FILTER_TERMS).optional(),
	eventType: filterTerm.optional(),
	/**
	* Matches `action` against a list (decision 6): a row passes when its `action` is one of the
	* entries. 1 to {@link AUDIT_READER_MAX_EVENT_TYPES} entries of 1 to
	* {@link AUDIT_READER_MAX_EVENT_TYPE_CHARS} UTF-16 code units. It sits where `eventType` sits,
	* in the caller's own filters, so a row it drops counts in neither `total` nor `filteredOut`, and a
	* segment whose `actionCounts` names none of the entries is skipped unread. Absent means no
	* list constraint. Duplicate entries are one entry.
	*/
	eventTypes: z.array(eventTypeTerm).min(1).max(64).optional(),
	excludeEventTypes: filterTerms,
	excludeAppEventTypes: filterTerms,
	/**
	* The actor=`system` view's include list (R-013). When present, a platform-authored row
	* (`source = 'platform'`) passes only when its `action` is listed; app rows follow
	* `appRowActor` and `excludeAppEventTypes` unchanged. Absent means no include constraint.
	*/
	includeOnlyEventTypes: filterTerms.optional(),
	appRowActor: z.enum(AUDIT_READER_ROW_ACTORS).optional(),
	/**
	* The `varies` verbs and the actor the view asks for (R-041), the mirror of
	* `AuditMergedListFilter.variesActor`. A platform row of a listed verb is a machine row when
	* its `actor_type` is not `human`, NULL included. `people` hides such a row; `system` admits
	* it beside `source = 'app'` and the include list, and with no `appRowActor` the include
	* list still gates every other row. Absent at actor `all`. An empty list adds no row.
	*/
	variesActor: z.strictObject({
		eventTypes: filterTerms,
		actor: z.enum(AUDIT_READER_ROW_ACTORS)
	}).optional(),
	page: z.number().int().min(1).max(AUDIT_READER_MAX_PAGE),
	pageSize: z.number().int().min(1).max(200),
	/**
	* The opaque position of the next page, copied from the previous answer's `nextCursor`
	* (decision 5). The reader mints it and binds it to the org, the window and the filters; a
	* cursor whose binding differs from this request is a 400 from the reader and reads nothing.
	* Its presence selects the keyset mode.
	*
	* Every 400 the reader gives for a cursor carries a `message` that starts
	* `audit-reader bad request: cursor:` and quotes no cursor byte: a cursor outside this
	* grammar, bytes that are not a cursor, and a binding mismatch. A refusal on any other field
	* never starts so. core-api turns that 400 into its own caller 400 `audit_cursor_refused`
	* only when its request carried a cursor (activity-paging R-003). The keyset fixture's
	* `cursorRefusals` pins the three bodies.
	*/
	cursor: cursorValue.optional(),
	/**
	* Whether the answer owes an exact `total` and `filteredOut` (decision 3). `false` selects the
	* keyset mode. `true` keeps the old mode and adds `countsExact: true` to the answer; its
	* segment budget charges read segments only, so it can pass where the request without the key
	* is a `window_too_wide` (R-005). Absent is the old mode.
	*/
	includeTotal: z.boolean().optional()
}).refine((q) => q.startDate === void 0 || q.endDate === void 0 || !auditInstantIsAfter(q.startDate, q.endDate), {
	message: "query: startDate must not be after endDate",
	path: ["endDate"]
}).refine((q) => q.eventType === void 0 || q.eventTypes === void 0, {
	message: "query: eventType and eventTypes are exclusive",
	path: ["eventTypes"]
}).refine((q) => q.cursor === void 0 && q.includeTotal !== false || q.page === 1, {
	message: "query: the keyset mode (a cursor, or includeTotal false) reads page 1 only",
	path: ["page"]
}).refine((q) => q.cursor === void 0 || q.includeTotal !== true, {
	message: "query: a cursor page owes no exact total, so includeTotal must not be true",
	path: ["includeTotal"]
}).refine((q) => q.objectIdAliases === void 0 || q.objectId !== void 0, {
	message: "query: objectIdAliases needs objectId",
	path: ["objectIdAliases"]
});
/**
* One row as the reader returns it: parquet schema v1's 27 columns less the four verdict
* columns (23 to 26), under their column names, timestamps as ISO instants, the four `*_enc`
* columns as base64 bytes that only core-api can open. The key order is the column order; a test
* pins the two lists equal. Every column is bounded: text at `AUDIT_READER_MAX_TERM_CHARS`,
* ciphertext at `AUDIT_READER_MAX_CIPHER_CHARS`, the subject list at
* `AUDIT_READER_MAX_ROW_SUBJECTS`. The fixture's `widestRow` is one row at every cap, and the
* reader proves it serves it (R-057).
*
* `related_object_id` (column 27, 2026-10-06-audit-second-id-filter) is the row's clear related
* id. Three input classes:
* 1. A string of 1 to `AUDIT_READER_MAX_TERM_CHARS` UTF-16 code units: the id the producer copied
*    from the declared `details` key. The envelope admits only the canonical id grammar, so a sealed
*    row carries a lowercase uuid; the row bound is the text cap, as on every text column.
* 2. `null`: the row has no related id, or its segment predates revision 3 (D-10).
* 3. Absent: a reader older than revision 3 answered. The key is `.nullable().optional()` so a
*    core-api parse never fails on an older reader (defaults row 9).
* An empty string and a string past the cap refuse.
*/
const auditReaderRowV1Schema = z.object({
	event_id: auditCanonicalIdSchema,
	org_id: z.string().regex(ORG_ID_RE),
	schema_version: z.number().int().min(1),
	recorded_at: isoInstant,
	occurred_at: isoInstant.nullable(),
	stream_seq: streamSeq,
	publisher_kid: codeUnitString().min(1).max(256).nullable(),
	actor_id: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	actor_type: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	action: filterTerm,
	object_type: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	object_id: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	app: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	source: z.enum(AUDIT_ACTION_COUNT_SOURCES),
	emitter: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	outcome: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	correlation_id: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable(),
	data_subjects: z.array(codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS)).max(AUDIT_READER_MAX_ROW_SUBJECTS),
	object_display_enc: cipherColumn.nullable(),
	ip_enc: cipherColumn.nullable(),
	user_agent_enc: cipherColumn.nullable(),
	details_enc: cipherColumn.nullable(),
	related_object_id: codeUnitString().min(1).max(AUDIT_READER_MAX_TERM_CHARS).nullable().optional()
});
/**
* The reader's answer: the page's rows newest first, the exact counts for the window
* under the request's filters, and what the scan cost. `truncated` is always `false`
* on a success. In the old mode a window the budget cannot serve is a `window_too_wide`
* refusal, never a partial page. In the keyset mode a budget or work bound after the first
* covered day ends the page early, with `nextCursor` and `searchedBackTo` (activity-paging
* R-022). Additive-safe: a consumer ignores a key it does not know.
*/
/** A window spans at most `AUDIT_READER_MAX_ANCHORS` closed org-days (a 90-day budget, with slack). */
const AUDIT_READER_MAX_ANCHORS = 400;
/**
* The largest `entries` or `rows` an anchor may carry (R-074). The bound is `entries`' Postgres
* `integer` column; `rows` is a `bigint` and is held to the same figure so both nets admit one
* set of anchors. A forged anchor past it refuses at the schema with `archive_integrity`, where
* the column answered a 500.
*/
const AUDIT_READER_MAX_ANCHOR_COUNT = 2147483647;
/**
* One closed org-day the reader loaded and verified to answer (R-014, R-043, I-9), whether or
* not the window kept any of its segments: the day manifest's digest, its entry count and the
* sum of its segment records' declared `rows`. `rows` is provenance and never a membership
* answer (DF-15: spans overlap). core-api pins `dayDigest` on the first read of a closed day and
* refuses a later read whose digest differs; it compares nothing else. A window touching more
* than `AUDIT_READER_MAX_ANCHORS` closed days is a `window_too_wide` refusal.
*/
const auditReaderDayAnchorV1Schema = z.strictObject({
	org: z.string().regex(ORG_ID_RE),
	date: z.string().regex(DATE_RE, "anchor: date YYYY-MM-DD").refine(isCalendarDay, "anchor: date is a calendar day"),
	dayDigest: z.string().regex(DIGEST_RE, "anchor: dayDigest sha256:<64 hex>"),
	entries: z.number().int().min(1).max(AUDIT_READER_MAX_ANCHOR_COUNT),
	rows: z.number().int().min(0).max(AUDIT_READER_MAX_ANCHOR_COUNT)
});
/**
* The reader's answer to one query. `relatedFilledFrom` (2026-10-06-audit-second-id-filter, D-11)
* says from when the `related_object_id` column can answer a `userId` read. Three input classes:
* 1. An ISO instant: the earliest `minRecordedAt` among the scanned segments whose
*    `related_object_id` column holds at least one value. The reader reads it from the footer
*    `null_count` it already loads on a `userId` scan. The reader does not clamp it to the
*    window (R-004). So the instant is a lower bound: it is never later than the first row that
*    can carry an id, and it can precede the request's `startDate` when a segment straddles it.
*    core-api adds 24 hours before it shows the value as `relatedSince` (stage 2).
* 2. `null`: the request carried a `userId`, and no scanned segment holds a related id.
* 3. Absent: the request carried no `userId`, or a reader older than revision 3 answered.
* Any other value refuses, as on every instant of this wire.
* In the keyset mode the scanned segments are the segments this page read, so the instant covers
* the span this page read and not the window (activity-paging R-023). Another page of the same
* walk can answer another instant.
*
* The answer, in both modes (activity-paging decisions 2, 3 and 5).
*
* Which of the three optional keys an answer carries is fixed by the request:
* 1. Old mode, no `includeTotal`: none of them, so the answer is today's bytes.
* 2. Old mode, `includeTotal: true`: `countsExact: true` alone.
* 3. Keyset mode (`cursor`, or `includeTotal: false`): all three, with `countsExact: false`.
* The schema refuses any other combination, so a reader that sends floors without saying so, or a
* cursor without the span it covered, is an unparseable body and never a silent wrong count.
*/
const auditReaderQueryResponseV1Schema = z.object({
	v: z.literal(1),
	rows: z.array(auditReaderRowV1Schema).max(200),
	total: z.number().int().min(0),
	filteredOut: z.number().int().min(0),
	scanned: z.strictObject({
		segments: z.number().int().min(0),
		bytes: z.number().int().min(0)
	}),
	/**
	* Every closed org-day the scan touched; empty when the window holds none (R-014). In the
	* keyset mode: every closed org-day of the window from the `searchedBackTo` day (or the
	* oldest day, when it is `null`) to the window end, the days newer than the cursor included,
	* so core-api reconciles the whole covered span (activity-paging R-004, decision 8).
	*/
	anchors: z.array(auditReaderDayAnchorV1Schema).max(400),
	truncated: z.literal(false),
	/**
	* Keyset mode only. The position of the next page, for the next request's `cursor` with the
	* same org, window and filters. `null` when no key of the window older than this page remains.
	* Two kinds of position, both opaque to every program but the reader:
	* 1. A full page names its last row, so the next page starts with the row after it.
	* 2. A string with fewer than `pageSize` rows names the `searchedBackTo` instant: a budget or
	*    work bound ended the walk, and the next page starts with the rows older than that instant
	*    (activity-paging R-022, R-023). The caller may continue.
	* The keyset fixture's `response` is the second kind and its `request.cursor` the first.
	*/
	nextCursor: cursorValue.nullable().optional(),
	/**
	* Keyset mode only. The oldest instant this answer covered: the reader considered every key
	* of the window at or after this instant and older than the request's cursor position (or at
	* or before the window end, on a first page). `null` when the reader covered the window back
	* to its `startDate`, or back to the oldest org-day the archive holds when the request has no
	* `startDate`. core-api reconciles the anchors over the covered span (decision 8).
	*/
	searchedBackTo: isoInstant.nullable().optional(),
	/**
	* Whether `total` and `filteredOut` are exact over the window. Absent in the old mode without
	* `includeTotal`, where they are exact; `true` for `includeTotal: true`; `false` in the keyset
	* mode, where they are floors over the span the reader read.
	*/
	countsExact: z.boolean().optional(),
	relatedFilledFrom: isoInstant.nullable().optional()
}).refine((r) => {
	const keysetKeys = (r.nextCursor === void 0 ? 0 : 1) + (r.searchedBackTo === void 0 ? 0 : 1);
	return keysetKeys === 0 ? r.countsExact !== false : keysetKeys === 2 && r.countsExact === false;
}, {
	message: "answer: nextCursor, searchedBackTo and countsExact false travel together, and only in the keyset mode",
	path: ["countsExact"]
});
/**
* The two refusals. `window_too_wide` (422) names the widest window in days the
* budget serves, so the caller narrows. In the keyset mode the reader gives it only when the
* first day the page covers passes the per-query budget alone (activity-paging R-022).
* `archive_integrity` (503) names the org-day whose chain failed verification or whose pinned
* `dayDigest` changed, and the problem.
*/
const auditReaderRefusalV1Schema = z.discriminatedUnion("code", [z.strictObject({
	v: z.literal(1),
	code: z.literal("window_too_wide"),
	status: z.literal(422),
	maxWindowDays: z.number().int().min(1),
	message: codeUnitString().min(1).max(512)
}), z.strictObject({
	v: z.literal(1),
	code: z.literal("archive_integrity"),
	status: z.literal(503),
	org: z.string().regex(ORG_ID_RE),
	date: z.string().regex(DATE_RE),
	problem: codeUnitString().min(1).max(512)
})]);
/**
* Parquet schema v1, the contract between the archiver's writer and every reader.
* Ids are UTF8 strings (R-007). Timestamps are `TIMESTAMP(MICROS, UTC)`.
* Encrypted columns are `BYTE_ARRAY` with the producer's version byte kept.
* `cloud/platform-sdk/schemas/audit-parquet-schema.v1.json` is the same table
* as a file; a test asserts the two are equal, and the Rust archiver builds its
* Arrow schema from the file.
*
* Columns 23 to 26 are the ingest verdict (2026-10-02-directory-asks-platform I12, I13, #3601).
* The archiver writes them; no envelope field carries them. All four are nullable, so a segment
* written before the verifier stays valid.
*
* - `verified` (23): `true` when the Ed25519 signature holds under a key of the publisher JWKS,
*   `false` when the archiver stores the row with a refusal reason from {@link AUDIT_VERIFY_REASONS}.
*   `NULL` when no verdict exists: the row predates the verifier, or the archiver ran with no
*   JWKS URL (R-018). `forged`, `malformed` and `unsigned` envelopes quarantine and write no row.
* - `verify_reason` (24): one of four states, keyed by `verified`.
*   1. `verified` is `true`: `NULL`.
*   2. `verified` is `false`: `unknown_key`, `expired_timestamp` or `unsupported_alg`.
*   3. `verified` is `NULL` and the archiver ran with no JWKS URL: `verifier_off` (R-018).
*   4. `verified` is `NULL` and the row predates the verifier: `NULL`.
* - `verifier` (25): the identity of the program that wrote a `true` or `false` verdict,
*   `<program>/<version>`, for example `audit-archiver/0.4.0`. `NULL` when `verified` is `NULL`.
* - `verified_at` (26): the time the archiver computed a `true` or `false` verdict. `NULL` when
*   `verified` is `NULL`.
*
* `publisher_kid` (7) stays the key id the verdict refers to.
*
* Column 27, `related_object_id`, is revision 3 (2026-10-06-audit-second-id-filter, D-3). It is a
* nullable clear UTF8 column that holds the envelope's `relatedObjectId`: the id of the second
* entity the vocabulary's `related` declaration names for the row's event type. `NULL` when the
* event type declares no related key, when the declared key holds no canonical id, or when the row
* was sealed before revision 3. It carries a bloom filter, so a `userId` read can rule a row group
* out on it as on `actor_id` and `object_id`. One row carries at most one related id.
*/
const AUDIT_PARQUET_SCHEMA_V1 = {
	version: 1,
	revision: 3,
	columns: [
		{
			id: 1,
			name: "event_id",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: false
		},
		{
			id: 2,
			name: "org_id",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: false
		},
		{
			id: 3,
			name: "schema_version",
			parquetType: "INT32",
			nullable: false
		},
		{
			id: 4,
			name: "recorded_at",
			parquetType: "INT64",
			logicalType: "TIMESTAMP_MICROS_UTC",
			nullable: false
		},
		{
			id: 5,
			name: "occurred_at",
			parquetType: "INT64",
			logicalType: "TIMESTAMP_MICROS_UTC",
			nullable: true
		},
		{
			id: 6,
			name: "stream_seq",
			parquetType: "INT64",
			nullable: false
		},
		{
			id: 7,
			name: "publisher_kid",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 8,
			name: "actor_id",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 9,
			name: "actor_type",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 10,
			name: "action",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: false
		},
		{
			id: 11,
			name: "object_type",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 12,
			name: "object_id",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 13,
			name: "app",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 14,
			name: "source",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: false
		},
		{
			id: 15,
			name: "emitter",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 16,
			name: "outcome",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 17,
			name: "correlation_id",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 18,
			name: "data_subjects",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: false,
			list: true,
			itemsNullable: false
		},
		{
			id: 19,
			name: "object_display_enc",
			parquetType: "BYTE_ARRAY",
			nullable: true
		},
		{
			id: 20,
			name: "ip_enc",
			parquetType: "BYTE_ARRAY",
			nullable: true
		},
		{
			id: 21,
			name: "user_agent_enc",
			parquetType: "BYTE_ARRAY",
			nullable: true
		},
		{
			id: 22,
			name: "details_enc",
			parquetType: "BYTE_ARRAY",
			nullable: true
		},
		{
			id: 23,
			name: "verified",
			parquetType: "BOOLEAN",
			nullable: true
		},
		{
			id: 24,
			name: "verify_reason",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 25,
			name: "verifier",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		},
		{
			id: 26,
			name: "verified_at",
			parquetType: "INT64",
			logicalType: "TIMESTAMP_MICROS_UTC",
			nullable: true
		},
		{
			id: 27,
			name: "related_object_id",
			parquetType: "BYTE_ARRAY",
			logicalType: "UTF8",
			nullable: true
		}
	],
	writer: {
		/** Rows within a segment are sorted so row-group statistics on `actor_id` prune. */
		sort: ["actor_id", "recorded_at"],
		compression: "ZSTD",
		/** 131_072 rows per row group: 2^17, about 15 MB at 114 B a row, under the 128 MiB lakehouse default. */
		rowGroupRows: 131072,
		/** The three columns a `userId` read matches; revision 3 added `related_object_id`. */
		bloomFilters: [
			"actor_id",
			"object_id",
			"related_object_id"
		],
		footerKey: "zion.audit.schema"
	}
};
/**
* Every value a stored row carries in `verify_reason` (column 24), in a fixed order, frozen
* (I13, R-018). The archiver keeps each such row, so the archive holds every real event.
*
* - `unknown_key`: the envelope's key id is absent from the publisher JWKS after one refresh.
*   The row has `verified = false`.
* - `expired_timestamp`: `signedAt` lies outside the verifier's clock window. `verified = false`.
* - `unsupported_alg`: the envelope names an algorithm other than `EdDSA`, for example `ES256`.
*   `verified = false`.
* - `verifier_off`: the archiver started with no `AUDIT_ARCHIVER_JWKS_URL`, so it checked no
*   signature. The row has `verified = NULL`, `verifier = NULL` and `verified_at = NULL` (R-018).
*
* A `forged`, `malformed` or `unsigned` envelope quarantines and has no row, so none of the three
* is a value of this list. A new reason only appends. The declaration stays on one line: the
* archiver's `verify.rs` test reads this line and compares it with its stored reasons.
*/
const AUDIT_VERIFY_REASONS = Object.freeze([
	"unknown_key",
	"expired_timestamp",
	"unsupported_alg",
	"verifier_off"
]);
//#endregion
export { AUDIT_ACTION_COUNTS_MAX_KEYS, AUDIT_ACTION_COUNT_KEY_RE, AUDIT_ACTION_COUNT_SOURCES, AUDIT_ARCHIVE_SCHEMA_VERSION, AUDIT_MAX_STREAM_SEQ, AUDIT_MIN_CALENDAR_DAY, AUDIT_PARQUET_SCHEMA_REVISION, AUDIT_PARQUET_SCHEMA_V1, AUDIT_PUBLISHER_KIDS_MAX, AUDIT_PUBLISHER_KID_MAX_CHARS, AUDIT_QUARANTINE_DLQ_KIND, AUDIT_READER_CURSOR_RE, AUDIT_READER_MAX_ANCHORS, AUDIT_READER_MAX_ANCHOR_COUNT, AUDIT_READER_MAX_CIPHER_CHARS, AUDIT_READER_MAX_CURSOR_CHARS, AUDIT_READER_MAX_EVENT_TYPES, AUDIT_READER_MAX_EVENT_TYPE_CHARS, AUDIT_READER_MAX_FILTER_TERMS, AUDIT_READER_MAX_PAGE, AUDIT_READER_MAX_PAGE_SIZE, AUDIT_READER_MAX_ROW_SUBJECTS, AUDIT_READER_MAX_TERM_CHARS, AUDIT_READER_OBJECT_ID_ALIAS_ACTIONS, AUDIT_READER_REFUSAL_CODES, AUDIT_READER_ROW_ACTORS, AUDIT_SCHEMA_OBJECT_KEY, AUDIT_SEAL_REASONS, AUDIT_VERIFY_REASONS, auditActionCountKey, auditChainCanonicalJson, auditChainDigest, auditChainInAppendOrder, auditChainSpans, auditDayManifestV1Schema, auditDayPrefix, auditManifestKey, auditOrgPrefix, auditQuarantineDlqRecordV1Schema, auditQuarantineSealRecordV1Schema, auditReaderDayAnchorV1Schema, auditReaderQueryRequestV1Schema, auditReaderQueryResponseV1Schema, auditReaderRefusalV1Schema, auditReaderRowV1Schema, auditSchemaObjectKey, auditSealKey, auditSealRecordV1Schema, auditSegmentKey, auditSegmentSealRecordV1Schema, auditVerifyChain };

//# sourceMappingURL=audit-archive-layout.js.map